import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import {
  NEW_FUNNEL_ROUTE,
  PROFILE_ROUTE,
  INSIGHTS_ACTIVITY_ROUTE,
  INSIGHTS_MARKET_OVERVIEW_ROUTE,
  SETTINGS_BILLING_ROUTE,
  SETTINGS_COMPANY_INFORMATION_ROUTE,
  SETTINGS_TEAM_ROUTE,
} from '@src/Routes/links';
import { capitalize } from '@src/util/capitalize';
import apolloClient from '@src/graphql/apollo';
import { IpDetailsDocument, IpDetailsQuery } from '@src/graphql/generated';
import { captureError } from '@src/util/error';
import { MetricsOpeningSocialShareParams, MetricsSetSalaryRangeParams } from './metrics.interface';
import { TrackEventParamsInterface, TrackEventParamsPayload } from './interfaces/trackEventParams.interface';
import { TrackPageViewParamsInterface } from './interfaces/trackPageViewParams.interface';
import { TrackRegisterParamsInterface } from './interfaces/trackRegisterParams.interface';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

const BUILD_VERSION = process.env.BUILD_VERSION;
const BUILD_TIMESTAMP = process.env.BUILD_TIMESTAMP;

async function areMetricsEnabled(): Promise<boolean> {
  try {
    const {
      data: { ipDetails },
    } = await apolloClient.query<IpDetailsQuery>({
      fetchPolicy: 'cache-first',
      query: IpDetailsDocument,
    });

    return !!ipDetails.isRealUser && process.env.ENV === 'production';
  } catch (error) {
    captureError(error);

    return process.env.ENV === 'production';
  }
}

function pushToDataLayer(value: unknown): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(value);
}

export async function trackEvent(params: TrackEventParamsInterface): Promise<void> {
  if (await areMetricsEnabled()) {
    pushToDataLayer({ event: 'ew-universal-event', ...params });

    return;
  }

  // eslint-disable-next-line no-console
  console.log('metrics.trackEvent', params);
}

export async function trackPageView(params: TrackPageViewParamsInterface): Promise<void> {
  if (await areMetricsEnabled()) {
    pushToDataLayer({ event: 'ew-page-view', ...params });

    return;
  }

  // eslint-disable-next-line no-console
  console.log('metrics.trackPageView', params);
}

export async function trackRegister(params: TrackRegisterParamsInterface): Promise<void> {
  if (await areMetricsEnabled()) {
    pushToDataLayer({ event: 'ew-universal-register', ...params });

    return;
  }

  // eslint-disable-next-line no-console
  console.log('metrics.trackRegister', params);
}

export async function trackSuperProperties(params: Record<string, unknown>): Promise<void> {
  params.payload = {
    ...params,
    BUILD_TIMESTAMP,
    BUILD_VERSION,
  };

  if (await areMetricsEnabled()) {
    pushToDataLayer({ event: 'ew-super-properties', ...params });

    return;
  }

  // eslint-disable-next-line no-console
  console.log('metrics.trackSuperProperties', params);
}

export async function trackViewCreatePost(payload: TrackEventParamsPayload): Promise<void> {
  await trackEvent({
    context: MetricContext.CREATE_OPENING,
    interaction: MetricAction.OPEN_POST_VIEW,
    payload: payload,
  });
}

export async function trackViewCreateTargeting(payload: TrackEventParamsPayload): Promise<void> {
  await trackEvent({
    context: MetricContext.CREATE_OPENING,
    interaction: MetricAction.OPEN_TARGETING_VIEW,
    payload: payload,
  });
}

export async function trackViewCreateMessage(payload: TrackEventParamsPayload): Promise<void> {
  await trackEvent({
    context: MetricContext.CREATE_OPENING,
    interaction: MetricAction.OPEN_MESSAGE_VIEW,
    payload: payload,
  });
}

export async function trackViewCreateApplication(payload: TrackEventParamsPayload): Promise<void> {
  await trackEvent({
    context: MetricContext.CREATE_OPENING,
    interaction: MetricAction.OPEN_INTERVIEW_VIEW,
    payload: payload,
  });
}

export async function trackViewCreateReview(payload: TrackEventParamsPayload): Promise<void> {
  await trackEvent({
    context: MetricContext.CREATE_OPENING,
    interaction: MetricAction.OPEN_REVIEW_VIEW,
    payload: payload,
  });
}

export async function trackSetSalaryRange({
  openingId,
  from,
  to,
  currency,
  period,
}: MetricsSetSalaryRangeParams): Promise<void> {
  if (!to) {
    return;
  }

  await trackEvent({
    context: MetricContext.CREATE_OPENING,
    interaction: MetricAction.SET_SALARY_RANGE,
    payload: {
      'Currency Code': currency,
      'Max Salary': +to,
      'Min Salary': from,
      'Opening Id': openingId,
      'Salary Period': capitalize(period),
    },
  });
}

export async function trackUpdateCompanyProfile(payload: TrackEventParamsPayload): Promise<void> {
  await trackEvent({
    context: MetricContext.UPDATING_COMPANY_PROFILE,
    interaction: MetricAction.FILL_COMPANY_PROFILE_INFO,
    payload: payload,
  });
}

export async function trackOpeningSocialShare({ platform, openingId }: MetricsOpeningSocialShareParams): Promise<void> {
  await trackEvent({
    context: MetricContext.OPENING_SHARING,
    interaction: MetricAction.SOCIAL_SHARE,
    payload: {
      'Event Source': MetricEventSource.SOCIAL_SHARE_BUTTON,
      'Opening Id': openingId,
      'Previous Context': MetricContext.OPENING_VIEW,
      'Social Media Platform': platform,
    },
  });
}

export async function trackOpenAddPaymentMethod(payload: TrackEventParamsPayload): Promise<void> {
  await trackEvent({
    context: MetricContext.ADD_PAYMENT_METHOD,
    interaction: MetricAction.OPEN_ADD_PAYMENT_METHOD_VIEW,
    payload: payload,
  });
}

export async function trackAddPaymentDetails(fieldType: string, type: string): Promise<void> {
  await trackEvent({
    context: MetricContext.ADD_PAYMENT_METHOD,
    interaction: MetricAction.FILL_PAYMENT_METHOD_INFO,
    payload: {
      'Field Type': fieldType,
      'Type': type,
    },
  });
}

export async function trackAddInvoiceDetails(fieldType: string): Promise<void> {
  trackAddPaymentDetails(fieldType, 'Invoice');
}

export async function trackAddCardDetails(fieldType: string): Promise<void> {
  await trackEvent({
    context: MetricContext.FINISH_RECRUITER_PROFILE,
    interaction: MetricAction.FILL_RECRUITER_PROFILE_INFO,
    payload: { 'Field Type': fieldType },
  });
}

export function getActiveMetricsContext(pathName: string): MetricContext | undefined {
  if (pathName.includes('/edit/message')) {
    return MetricContext.CREATE_OPENING_MESSAGE;
  } else if (pathName.includes('/edit/questions')) {
    return MetricContext.CREATE_OPENING_INTERVIEW;
  } else if (pathName.includes('/review')) {
    return MetricContext.CREATE_OPENING_REVIEW;
  } else if (pathName.includes('/edit')) {
    return MetricContext.CREATE_OPENING_TARGETING;
  } else if (pathName.includes('/chats')) {
    return MetricContext.CHATS;
  } else if (pathName.includes(NEW_FUNNEL_ROUTE)) {
    return MetricContext.CREATE_OPENING;
  } else if (pathName.includes('/funnel')) {
    return MetricContext.OPENING_VIEW;
  } else if (pathName.includes(SETTINGS_COMPANY_INFORMATION_ROUTE)) {
    return MetricContext.COMPANY_BILLING_INFORMATION;
  } else if (pathName.includes('/company')) {
    return MetricContext.COMPANY_PROFILE;
  } else if (pathName.includes(PROFILE_ROUTE)) {
    return MetricContext.RECRUITER_PROFILE;
  } else if (pathName.includes(INSIGHTS_ACTIVITY_ROUTE)) {
    return MetricContext.ACTIVITY_FEED;
  } else if (pathName.includes(INSIGHTS_MARKET_OVERVIEW_ROUTE)) {
    return MetricContext.MARKET_OVERVIEW;
  } else if (pathName.includes(SETTINGS_BILLING_ROUTE)) {
    return MetricContext.BILLING;
  } else if (pathName.includes(SETTINGS_TEAM_ROUTE)) {
    return MetricContext.TEAM_MEMBERS;
  }
}
